import { useCallback, useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import { MobileBottomTab } from './MobileBottomTab';
import { MobileNavSheet } from './MobileNavSheet';
import { CommandPalette } from './CommandPalette';
import { TenantLocaleSync } from '@/components/i18n/TenantLocaleSync';

const SIDEBAR_COLLAPSED_KEY = 'invirtus.shell.sidebarCollapsed';

export const AppShell = () => {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(collapsed));
    } catch {
      // ignore
    }
  }, [collapsed]);

  const openPalette = useCallback(() => setPaletteOpen(true), []);
  const closePalette = useCallback(() => setPaletteOpen(false), []);

  // Global ⌘K / Ctrl+K — toggles the command palette.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const isModK =
        (e.key === 'k' || e.key === 'K') && (e.metaKey || e.ctrlKey);
      if (isModK) {
        e.preventDefault();
        setPaletteOpen((open) => !open);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  return (
    <div className="h-screen bg-background-gray overflow-hidden">
      {/* Side-effect: align the UI locale with the tenant's configured
          default_language on every workspace fetch. Renders nothing. */}
      <TenantLocaleSync />

      {/* Desktop sidebar — position: fixed, robust under zoom */}
      <Sidebar collapsed={collapsed} onToggleCollapsed={() => setCollapsed((c) => !c)} />

      {/* Mobile bottom-tab — fixed at bottom of viewport, hidden on md+ */}
      <MobileBottomTab onOpenMore={() => setMobileNavOpen(true)} />

      {/* Main content column. Definite height (h-screen on outer + flex-col here) so pages
          can use h-full / flex-1 and have it resolve. Topbar is a flex sibling, main scrolls
          internally — one scroll context per page, no double-scroll, no zoom math. */}
      <div
        className={`flex h-screen flex-col transition-[padding] duration-200 ease-out pb-14 md:pb-0 ${
          collapsed ? 'md:pl-14' : 'md:pl-60'
        }`}
      >
        <Topbar
          onOpenMobileNav={() => setMobileNavOpen(true)}
          onOpenSearch={openPalette}
        />
        <main className="flex-1 min-h-0 min-w-0 overflow-y-auto">
          <Outlet />
        </main>
      </div>

      <MobileNavSheet open={mobileNavOpen} onClose={() => setMobileNavOpen(false)} />
      <CommandPalette open={paletteOpen} onClose={closePalette} />
    </div>
  );
};
